import React, { Fragment, useState, useEffect } from 'react';
import { risksApi, usersApi, processesApi } from '../api';
import ExportButtons from '../components/ExportButtons';
import ColumnSelector from '../components/ColumnSelector';
import useColumnVisibility from '../hooks/useColumnVisibility';
import { toast } from 'sonner';
import { validateDateFields, extractApiError } from '../utils/dateValidation';
import {
  RefreshCw,
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Loader2,
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
  Eye,
  TrendingUp,
  Grid3X3,
} from 'lucide-react';

const CATEGORIES = [
  { value: 'strategic', label: 'Estratégico' },
  { value: 'operational', label: 'Operacional' },
  { value: 'financial', label: 'Financeiro' },
  { value: 'compliance', label: 'Conformidade' },
  { value: 'quality', label: 'Qualidade' },
  { value: 'safety', label: 'Segurança' },
  { value: 'environmental', label: 'Ambiental' },
  { value: 'reputational', label: 'Reputação' },
  { value: 'other', label: 'Outros' },
];

const PROBABILITIES = [
  { value: 'very_low', label: 'Muito Baixa (1)', num: 1 },
  { value: 'low', label: 'Baixa (2)', num: 2 },
  { value: 'medium', label: 'Média (3)', num: 3 },
  { value: 'high', label: 'Alta (4)', num: 4 },
  { value: 'very_high', label: 'Muito Alta (5)', num: 5 },
];

const IMPACTS = [
  { value: 'insignificant', label: 'Insignificante (1)', num: 1 },
  { value: 'minor', label: 'Menor (2)', num: 2 },
  { value: 'moderate', label: 'Moderado (3)', num: 3 },
  { value: 'major', label: 'Maior (4)', num: 4 },
  { value: 'catastrophic', label: 'Catastrófico (5)', num: 5 },
];

const TREATMENTS = [
  { value: 'avoid', label: 'Evitar' },
  { value: 'mitigate', label: 'Mitigar' },
  { value: 'transfer', label: 'Transferir' },
  { value: 'accept', label: 'Aceitar' },
];

const STATUS_CONFIG = {
  identified: { label: 'Identificado', color: 'badge-gray', icon: Shield },
  analyzing: { label: 'Em Análise', color: 'badge-blue', icon: RefreshCw },
  treating: { label: 'Em Tratamento', color: 'badge-yellow', icon: Clock },
  monitoring: { label: 'Monitorando', color: 'badge-blue', icon: Eye },
  closed: { label: 'Encerrado', color: 'badge-green', icon: CheckCircle },
  accepted: { label: 'Aceito', color: 'badge-gray', icon: CheckCircle },
};

const LEVEL_CONFIG = {
  low: { label: 'Baixo', color: 'badge-green' },
  medium: { label: 'Médio', color: 'badge-yellow' },
  high: { label: 'Alto', color: 'badge-orange' },
  critical: { label: 'Crítico', color: 'badge-red' },
};

const Risks = () => {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showMatrixModal, setShowMatrixModal] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [matrixData, setMatrixData] = useState([]);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'operational',
    process: '',
    area: '',
    source: '',
    initial_probability: 'medium',
    initial_impact: 'moderate',
    treatment_type: '',
    treatment_description: '',
    owner_id: '',
    review_date: '',
    is_opportunity: false,
  });
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [processes, setProcesses] = useState([]);

  useEffect(() => {
    loadData();
    loadUsers();
    loadProcesses();
  }, [filterCategory, filterLevel]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterCategory) params.category = filterCategory;
      if (filterLevel) params.level = filterLevel;

      const [risksRes, statsRes] = await Promise.all([
        risksApi.list(params),
        risksApi.stats(),
      ]);
      setRisks(risksRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadProcesses = async () => {
    try {
      const response = await processesApi.listActive();
      setProcesses(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar processos:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await usersApi.list({ is_active: true });
      setUsers(res.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadMatrix = async () => {
    try {
      const res = await risksApi.matrix();
      setMatrixData(res.data.risks);
      setShowMatrixModal(true);
    } catch (error) {
      toast.error('Erro ao carregar matriz');
    }
  };

  const handleOpenModal = async (risk = null) => {
    if (risk) {
      try {
        const res = await risksApi.get(risk.id);
        const fullRisk = res.data;
        setEditingRisk(fullRisk);
        setFormData({
          title: fullRisk.title,
          description: fullRisk.description || '',
          category: fullRisk.category,
          process: fullRisk.process || '',
          area: fullRisk.area || '',
          source: fullRisk.source || '',
          initial_probability: fullRisk.initial_probability,
          initial_impact: fullRisk.initial_impact,
          treatment_type: fullRisk.treatment_type || '',
          treatment_description: fullRisk.treatment_description || '',
          owner_id: fullRisk.owner_id || '',
          review_date: fullRisk.review_date || '',
          is_opportunity: fullRisk.is_opportunity,
        });
        setShowModal(true);
      } catch (error) {
        toast.error('Erro ao carregar dados do risco');
      }
    } else {
      setEditingRisk(null);
      setFormData({
        title: '',
        description: '',
        category: 'operational',
        process: '',
        area: '',
        source: '',
        initial_probability: 'medium',
        initial_impact: 'moderate',
        treatment_type: '',
        treatment_description: '',
        owner_id: '',
        review_date: '',
        is_opportunity: false,
      });
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRisk(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title) {
      toast.error('Preencha o título');
      return;
    }

    if (!formData.review_date) {
      toast.error('Preencha a Data de Revisão para continuar');
      return;
    }

    const dateError = validateDateFields([
      { value: formData.review_date, label: 'Data de Revisão' },
    ]);
    if (dateError) {
      toast.error(dateError);
      return;
    }

    setSaving(true);

    try {
      const data = {
        ...formData,
        current_probability: formData.initial_probability,
        current_impact: formData.initial_impact,
        owner_id: formData.owner_id || null,
        treatment_type: formData.treatment_type || null,
        review_date: formData.review_date || null,
      };

      if (editingRisk) {
        await risksApi.update(editingRisk.id, data);
        toast.success('Risco atualizado!');
      } else {
        await risksApi.create(data);
        toast.success('Risco registrado!');
      }
      handleCloseModal();
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao salvar'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (risk) => {
    if (!window.confirm(`Excluir risco "${risk.code}"?`)) return;

    try {
      await risksApi.delete(risk.id);
      toast.success('Risco excluído!');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleViewDetails = async (risk) => {
    try {
      const res = await risksApi.get(risk.id);
      setSelectedRisk(res.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const filteredRisks = risks.filter(r =>
    r.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.area?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.identified;
    const Icon = config.icon;
    return (
      <span className={`badge ${config.color}`}>
        <Icon size={12} className="mr-1" />
        {config.label}
      </span>
    );
  };

  const LevelBadge = ({ level }) => {
    const config = LEVEL_CONFIG[level] || LEVEL_CONFIG.medium;
    return <span className={`badge ${config.color}`}>{config.label}</span>;
  };

  // Risk Matrix Component
  const RiskMatrix = () => {
    const getMatrixColor = (prob, impact) => {
      const score = prob * impact;
      if (score <= 4) return 'bg-green-500/80';
      if (score <= 9) return 'bg-yellow-500/80';
      if (score <= 16) return 'bg-orange-500/80';
      return 'bg-red-500/80';
    };

    const getRisksInCell = (prob, impact) => {
      return matrixData.filter(r => r.probability_value === prob && r.impact_value === impact);
    };

    return (
      <div className="table-container">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="p-2 text-xs text-gray-400"></th>
              {IMPACTS.map(i => (
                <th key={i.value} className="p-2 text-xs text-gray-400 text-center border border-gray-700">
                  {i.label.split(' ')[0]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...PROBABILITIES].reverse().map(p => (
              <tr key={p.value}>
                <td className="p-2 text-xs text-gray-400 border border-gray-700 w-24">
                  {p.label.split(' ')[0]}
                </td>
                {IMPACTS.map(i => {
                  const cellRisks = getRisksInCell(p.num, i.num);
                  return (
                    <td
                      key={`${p.value}-${i.value}`}
                      className={`p-2 border border-gray-700 ${getMatrixColor(p.num, i.num)} h-20 w-32 align-top`}
                    >
                      <div className="flex flex-wrap gap-1 text-white">
                        {cellRisks.map(r => (
                          <span
                            key={r.id}
                            className="text-xs bg-gray-900/50 px-1 py-0.5 rounded cursor-pointer hover:bg-gray-800"
                            title={r.title}
                          >
                            {r.code}
                          </span>
                        ))}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex gap-4 mt-4 text-xs text-white">
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-green-500/80 rounded"></span> Baixo (1-4)</span>
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-yellow-500/80 rounded"></span> Médio (5-9)</span>
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-orange-500/80 rounded"></span> Alto (10-16)</span>
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-red-500/80 rounded"></span> Crítico (17-25)</span>
        </div>
      </div>
    );
  };

  const COLUMNS = [
    { key: 'code', label: 'Código', locked: true, render: (risk) => (
      <td className="font-mono text-green-400">
        {risk.code}
        {risk.is_opportunity && <TrendingUp size={12} className="inline ml-1 text-green-400" title="Oportunidade" />}
      </td>
    ) },
    { key: 'titulo', label: 'Título', render: (risk) => <td><div className="max-w-xs truncate">{risk.title}</div></td> },
    { key: 'categoria', label: 'Categoria', render: (risk) => <td>{CATEGORIES.find(c => c.value === risk.category)?.label || risk.category}</td> },
    { key: 'nivel', label: 'Nível', render: (risk) => <td><LevelBadge level={risk.current_level || risk.initial_level} /></td> },
    { key: 'status', label: 'Status', render: (risk) => <td><StatusBadge status={risk.status} /></td> },
    { key: 'area', label: 'Área', render: (risk) => <td>{risk.area || '-'}</td> },
    { key: 'revisao', label: 'Revisão', render: (risk) => <td>{risk.review_date ? new Date(risk.review_date).toLocaleDateString('pt-BR') : '-'}</td> },
    { key: 'actions', label: 'Ações', locked: true, headerClass: 'text-right', render: (risk) => (
      <td className="text-right">
        <div className="flex justify-end gap-2">
          <button
            onClick={() => handleViewDetails(risk)}
            className="p-1 hover:text-blue-400 transition-colors"
            title="Ver detalhes"
          >
            <Eye size={16} />
          </button>
          <button
            onClick={() => handleOpenModal(risk)}
            className="p-1 hover:text-green-400 transition-colors"
            title="Editar"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => handleDelete(risk)}
            className="p-1 hover:text-red-400 transition-colors"
            title="Excluir"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </td>
    ) },
  ];

  const { visibleColumns, visibleKeys, toggleColumn, resetColumns } = useColumnVisibility({ storageKey: 'risks', columns: COLUMNS });

  return (
    <div className="space-y-6" data-testid="risks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestão de Riscos</h1>
          <p className="text-gray-400 text-sm">Identificação, análise e tratamento de riscos</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadMatrix} className="btn-secondary" data-testid="matrix-btn">
            <Grid3X3 size={20} />
            Matriz
          </button>
          <ColumnSelector columns={COLUMNS} visibleKeys={visibleKeys} onToggle={toggleColumn} onReset={resetColumns} />
          <button onClick={() => handleOpenModal()} className="btn-primary" data-testid="new-risk-btn">
            <Plus size={20} />
            Novo Risco
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Total</p>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Ameaças</p>
            <p className="text-2xl font-bold text-red-500">{stats.threats}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Oportunidades</p>
            <p className="text-2xl font-bold text-green-500">{stats.opportunities}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Críticos/Altos</p>
            <p className="text-2xl font-bold text-orange-500">{stats.critical_high}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Baixo Risco</p>
            <p className="text-2xl font-bold text-green-500">{stats.by_level?.low || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por código, título ou área..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10 w-full"
              data-testid="search-input"
            />
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="input px-3 w-full sm:w-60"
            data-testid="category-filter"
          >
            <option value="">Todas Categorias</option>
            {CATEGORIES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          <select
            value={filterLevel}
            onChange={(e) => setFilterLevel(e.target.value)}
            className="input px-3 w-full sm:w-60"
            data-testid="level-filter"
          >
            <option value="">Todos Níveis</option>
            {Object.entries(LEVEL_CONFIG).map(([value, config]) => (
              <option key={value} value={value}>{config.label}</option>
            ))}
          </select>
          <button onClick={loadData} className="btn-secondary" data-testid="refresh-btn">
            <RefreshCw size={20} />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : filteredRisks.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            Nenhum risco encontrado
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  {visibleColumns.map(col => (
                    <th key={col.key} className={col.headerClass || ''}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRisks.map((risk) => (
                  <tr key={risk.id} data-testid={`risk-row-${risk.id}`}>
                    {visibleColumns.map(col => (
                      <Fragment key={col.key}>{col.render(risk)}</Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6 max-w-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                {editingRisk ? 'Editar Risco' : 'Novo Risco'}
              </h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Título *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="input w-full"
                    placeholder="Título do risco"
                    data-testid="risk-title-input"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="is_opportunity"
                    checked={formData.is_opportunity}
                    onChange={(e) => setFormData({ ...formData, is_opportunity: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label htmlFor="is_opportunity" className="text-gray-300">Oportunidade</label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input w-full"
                  rows={2}
                  placeholder="Descreva o risco"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Categoria</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="input w-full"
                  >
                    {CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Área</label>
                  <input
                    type="text"
                    value={formData.area}
                    onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                    className="input w-full"
                    placeholder="Ex: Produção..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Processo</label>
                  <select
                    value={formData.process}
                    onChange={(e) => setFormData({ ...formData, process: e.target.value })}
                    className="input w-full"
                  >
                    <option value="">Selecione um processo</option>
                    {processes.map(p => (
                      <option key={p.id} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Fonte do Risco</label>
                <input
                  type="text"
                  value={formData.source}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                  className="input w-full"
                  placeholder="Origem ou causa do risco"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Probabilidade</label>
                  <select
                    value={formData.initial_probability}
                    onChange={(e) => setFormData({ ...formData, initial_probability: e.target.value })}
                    className="input w-full"
                  >
                    {PROBABILITIES.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Impacto</label>
                  <select
                    value={formData.initial_impact}
                    onChange={(e) => setFormData({ ...formData, initial_impact: e.target.value })}
                    className="input w-full"
                  >
                    {IMPACTS.map(i => (
                      <option key={i.value} value={i.value}>{i.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Tipo de Tratamento</label>
                  <select
                    value={formData.treatment_type}
                    onChange={(e) => setFormData({ ...formData, treatment_type: e.target.value })}
                    className="input w-full"
                  >
                    <option value="">Selecione...</option>
                    {TREATMENTS.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Responsável</label>
                  <select
                    value={formData.owner_id}
                    onChange={(e) => setFormData({ ...formData, owner_id: e.target.value })}
                    className="input w-full"
                  >
                    <option value="">Selecione...</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.full_name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Descrição do Tratamento</label>
                <textarea
                  value={formData.treatment_description}
                  onChange={(e) => setFormData({ ...formData, treatment_description: e.target.value })}
                  className="input w-full"
                  rows={2}
                  placeholder="Descreva como o risco será tratado"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Data de Revisão *</label>
                <input
                  type="date"
                  value={formData.review_date}
                  onChange={(e) => setFormData({ ...formData, review_date: e.target.value })}
                  className="input w-full"
                  min="1900-01-01"
                  max="2099-12-31"
                  required
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={handleCloseModal} className="btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={saving} data-testid="risk-submit-btn">
                  {saving ? <Loader2 className="animate-spin" size={20} /> : editingRisk ? 'Salvar' : 'Registrar'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* Matrix Modal */}
      {showMatrixModal && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6 max-w-4xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Matriz de Riscos</h2>
              <button onClick={() => setShowMatrixModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <RiskMatrix />
          </div>
        </>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedRisk && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6 max-w-3xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-white">{selectedRisk.code}</h2>
                <p className="text-gray-400">{selectedRisk.title}</p>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <StatusBadge status={selectedRisk.status} />
                <LevelBadge level={selectedRisk.current_level || selectedRisk.initial_level} />
                {selectedRisk.is_opportunity && (
                  <span className="badge badge-green">Oportunidade</span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 text-sm">Categoria</p>
                  <p className="text-white">{CATEGORIES.find(c => c.value === selectedRisk.category)?.label}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Área</p>
                  <p className="text-white">{selectedRisk.area || '-'}</p>
                </div>
              </div>

              {selectedRisk.description && (
                <div>
                  <p className="text-gray-400 text-sm">Descrição</p>
                  <p className="text-white whitespace-pre-wrap">{selectedRisk.description}</p>
                </div>
              )}

              <div className="bg-gray-800 rounded-lg p-4">
                <h3 className="text-white font-semibold mb-3">Avaliação</h3>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-gray-400 text-xs">Probabilidade</p>
                    <p className="text-white font-semibold">
                      {PROBABILITIES.find(p => p.value === (selectedRisk.current_probability || selectedRisk.initial_probability))?.label.split(' ')[0]}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">Impacto</p>
                    <p className="text-white font-semibold">
                      {IMPACTS.find(i => i.value === (selectedRisk.current_impact || selectedRisk.initial_impact))?.label.split(' ')[0]}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">Score</p>
                    <p className="text-white font-semibold">{selectedRisk.current_score || selectedRisk.initial_score}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">Nível</p>
                    <LevelBadge level={selectedRisk.current_level || selectedRisk.initial_level} />
                  </div>
                </div>
              </div>

              {selectedRisk.treatment_type && (
                <div>
                  <p className="text-gray-400 text-sm">Tratamento: {TREATMENTS.find(t => t.value === selectedRisk.treatment_type)?.label}</p>
                  {selectedRisk.treatment_description && (
                    <p className="text-white whitespace-pre-wrap">{selectedRisk.treatment_description}</p>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button onClick={() => setShowDetailModal(false)} className="btn-secondary">
                  Fechar
                </button>
                <button
                  onClick={() => { setShowDetailModal(false); handleOpenModal(selectedRisk); }}
                  className="btn-primary"
                >
                  <Edit size={16} />
                  Editar
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Risks;
